import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';

import { apiGet, apiPatch, apiPost } from '../lib/api';

type ProjectStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';
type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'void' | 'overdue';
type Tab = 'overview' | 'invoices' | 'usage' | 'settings';
type OpenAiPeriodPreset = 'this_month' | 'last_month' | 'last_3_months';

interface Project {
  id: string;
  client_id: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  billing_type: string;
  billing_cycle: string;
  markup_pct: number | null;
  monthly_cap_usd: number | null;
  fixed_total_usd: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ClientInfo {
  id: string;
  display_name: string;
  display_email: string | null;
}

interface InvoiceRow {
  id: string;
  invoice_number: string;
  status: InvoiceStatus;
  currency: string;
  subtotal: number;
  tax_amount: number;
  total: number;
  issue_date: string;
  due_date: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface UsageSummary {
  from: string;
  to: string;
  totals: {
    raw_cost_usd: number;
    billable_amount_usd: number;
    requests_count: number;
    input_tokens: number;
    output_tokens: number;
  };
  by_model: Array<{
    provider: string;
    model: string;
    total_input: number;
    total_output: number;
    total_requests: number;
    total_raw: number;
    total_billable: number;
  }>;
}

interface OpenAiProjectMonthlyUsage {
  from: string;
  to: string;
  mappings: Array<{
    key_identifier: string;
    label: string | null;
    is_active: boolean;
  }>;
  totals: {
    raw_cost_usd: number;
    billable_amount_usd: number;
    requests_count: number;
    input_tokens: number;
    output_tokens: number;
  };
  monthly: Array<{
    month: string;
    total_raw_usd: number;
    total_billable_usd: number;
    total_requests: number;
    total_input_tokens: number;
    total_output_tokens: number;
  }>;
  live_preview?:
    | {
        status: 'ok';
        matched_rows: number;
        totals: {
          requests_count: number;
          input_tokens: number;
          output_tokens: number;
        };
        estimated_cost: {
          raw_cost_usd: number;
          billable_amount_usd: number;
        };
      }
    | {
        status: 'error';
        error: string;
      };
}

interface InvoiceLineDraft {
  id: string;
  item_name: string;
  description: string;
  quantity: string;
  unit_price: string;
}

interface ItemTemplate {
  id: string;
  name: string;
  default_unit_price: number | null;
  currency: string | null;
  category: string | null;
  is_active: boolean;
}

interface CurrencyFinancialSummary {
  currency: string;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
}

const allowedTransitions: Record<ProjectStatus, ProjectStatus[]> = {
  draft: ['active', 'archived'],
  active: ['paused', 'completed'],
  paused: ['active', 'completed'],
  completed: ['archived'],
  archived: [],
};

const transitionLabels: Record<string, string> = {
  active: 'Activate',
  paused: 'Pause',
  completed: 'Complete',
  archived: 'Archive',
};

export function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [openAiMonthlyUsage, setOpenAiMonthlyUsage] = useState<OpenAiProjectMonthlyUsage | null>(
    null,
  );
  const [openAiMonthlyLoading, setOpenAiMonthlyLoading] = useState(false);
  const [openAiPeriodPreset, setOpenAiPeriodPreset] = useState<OpenAiPeriodPreset>('last_3_months');
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [transitionBusy, setTransitionBusy] = useState(false);

  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceIssueDate, setInvoiceIssueDate] = useState(todayDateValue());
  const [invoiceDueDate, setInvoiceDueDate] = useState(todayDateValue(14));
  const [invoiceCurrency, setInvoiceCurrency] = useState('USD');
  const [invoiceTaxAmount, setInvoiceTaxAmount] = useState('0');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoiceInternalNotes, setInvoiceInternalNotes] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineDraft[]>([
    { id: rowId(), item_name: '', description: 'Project service', quantity: '1', unit_price: '' },
  ]);
  const [lineItemTemplateSelections, setLineItemTemplateSelections] = useState<
    Record<string, string>
  >({});
  const [itemTemplates, setItemTemplates] = useState<ItemTemplate[]>([]);
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);

  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editBillingType, setEditBillingType] = useState('usage_based');
  const [editBillingCycle, setEditBillingCycle] = useState('monthly');
  const [editMarkup, setEditMarkup] = useState('');
  const [editCap, setEditCap] = useState('');
  const [editFixedTotal, setEditFixedTotal] = useState('');

  useEffect(() => {
    if (!id) return;
    void loadProject();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    void loadOpenAiMonthlyUsage(id, openAiPeriodPreset);
  }, [id, openAiPeriodPreset]);

  useEffect(() => {
    if (!project) return;
    setEditName(project.name);
    setEditDesc(project.description ?? '');
    setEditBillingType(project.billing_type);
    setEditBillingCycle(project.billing_cycle);
    setEditMarkup(project.markup_pct !== null ? String(Math.round(project.markup_pct * 100)) : '');
    setEditCap(project.monthly_cap_usd !== null ? String(project.monthly_cap_usd) : '');
    setEditFixedTotal(project.fixed_total_usd !== null ? String(project.fixed_total_usd) : '');
  }, [project]);

  async function loadProject(): Promise<void> {
    if (!id) return;
    setError(null);
    try {
      const [proj, invs, templates] = await Promise.all([
        apiGet<Project>(`/api/projects/${id}`),
        apiGet<InvoiceRow[]>(`/api/projects/${id}/invoices`),
        apiGet<ItemTemplate[]>('/api/settings/item-templates').catch(() => []),
      ]);
      setProject(proj);
      setInvoices(invs);
      setItemTemplates(templates.filter((template) => template.is_active));

      const [client, usage] = await Promise.all([
        apiGet<ClientInfo>(`/api/clients/${proj.client_id}`).catch(() => null),
        apiGet<UsageSummary>(`/api/projects/${id}/usage/summary`).catch(() => null),
      ]);
      setClientInfo(client);
      setUsageSummary(usage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    }
  }

  async function loadOpenAiMonthlyUsage(
    projectId: string,
    preset: OpenAiPeriodPreset,
  ): Promise<void> {
    setOpenAiMonthlyLoading(true);
    const range = resolveOpenAiRange(preset);
    const query = new URLSearchParams({ from: range.from, to: range.to });
    try {
      const data = await apiGet<OpenAiProjectMonthlyUsage>(
        `/api/projects/${projectId}/usage/openai-monthly?${query.toString()}&include_live=true`,
      );
      setOpenAiMonthlyUsage(data);
    } catch {
      setOpenAiMonthlyUsage(null);
    } finally {
      setOpenAiMonthlyLoading(false);
    }
  }

  async function handleTransition(nextStatus: ProjectStatus): Promise<void> {
    if (!id) return;
    setTransitionBusy(true);
    setError(null);
    try {
      const updated = await apiPatch<Project>(
        `/api/projects/${id}`,
        { status: nextStatus },
        { successMessage: `Project ${transitionLabels[nextStatus]?.toLowerCase() ?? 'updated'}` },
      );
      setProject(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setTransitionBusy(false);
    }
  }

  async function handleSaveSettings(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);
    const markupPct = editMarkup ? Number(editMarkup) / 100 : undefined;
    const cap = editCap ? Number(editCap) : undefined;
    const fixedTotal = editFixedTotal ? Number(editFixedTotal) : undefined;

    try {
      const updated = await apiPatch<Project>(
        `/api/projects/${id}`,
        {
          name: editName,
          description: editDesc || null,
          billing_type: editBillingType,
          billing_cycle: editBillingCycle,
          ...(markupPct !== undefined ? { markup_pct: markupPct } : {}),
          ...(cap !== undefined ? { monthly_cap_usd: cap } : {}),
          ...(fixedTotal !== undefined ? { fixed_total_usd: fixedTotal } : {}),
        },
        { successMessage: 'Project settings saved' },
      );
      setProject(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateInvoice(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!project) return;

    const taxAmount = Number(invoiceTaxAmount);
    if (Number.isNaN(taxAmount) || taxAmount < 0) {
      setError('Tax amount must be a non-negative number');
      return;
    }
    const issueDateIso = toIsoFromDateInput(invoiceIssueDate);
    const dueDateIso = toIsoFromDateInput(invoiceDueDate);
    if (!issueDateIso || !dueDateIso) {
      setError('Issue date and due date must be valid dates');
      return;
    }

    const normalizedItems: Array<{
      item_name: string;
      description: string;
      quantity: number;
      unit_price: number;
    }> = [];
    for (const item of lineItems) {
      const quantity = Number(item.quantity);
      const unitPrice = Number(item.unit_price);
      if (!item.item_name.trim()) {
        setError('Line item name is required');
        return;
      }
      if (!item.description.trim()) {
        setError('Line item description is required');
        return;
      }
      if (Number.isNaN(quantity) || quantity <= 0) {
        setError('Line item quantity must be > 0');
        return;
      }
      if (!item.unit_price.trim() || Number.isNaN(unitPrice) || unitPrice < 0) {
        setError('Line item unit price is required');
        return;
      }
      normalizedItems.push({
        item_name: item.item_name.trim(),
        description: item.description.trim(),
        quantity,
        unit_price: unitPrice,
      });
    }

    setCreatingInvoice(true);
    setError(null);
    try {
      await apiPost(
        `/api/projects/${project.id}/invoices`,
        {
          issue_date: issueDateIso,
          due_date: dueDateIso,
          currency: invoiceCurrency,
          tax_amount: taxAmount,
          notes: invoiceNotes || undefined,
          internal_notes: invoiceInternalNotes || undefined,
          line_items: normalizedItems,
        },
        { successMessage: 'Invoice created' },
      );
      const nextRowId = rowId();
      setLineItems([
        {
          id: nextRowId,
          item_name: '',
          description: 'Project service',
          quantity: '1',
          unit_price: '',
        },
      ]);
      setLineItemTemplateSelections({ [nextRowId]: 'custom' });
      setInvoiceCurrency('USD');
      setInvoiceTaxAmount('0');
      setInvoiceNotes('');
      setInvoiceInternalNotes('');
      setShowInvoiceForm(false);
      const updatedInvoices = await apiGet<InvoiceRow[]>(`/api/projects/${project.id}/invoices`);
      setInvoices(updatedInvoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  }

  async function sendInvoice(invoiceId: string): Promise<void> {
    setBusyInvoiceId(invoiceId);
    setError(null);
    try {
      await apiPost(`/api/invoices/${invoiceId}/send`, {}, { successMessage: 'Invoice sent' });
      if (id) {
        const updatedInvoices = await apiGet<InvoiceRow[]>(`/api/projects/${id}/invoices`);
        setInvoices(updatedInvoices);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setBusyInvoiceId(null);
    }
  }

  async function voidInvoice(invoiceId: string): Promise<void> {
    setBusyInvoiceId(invoiceId);
    setError(null);
    try {
      await apiPost(`/api/invoices/${invoiceId}/void`, {}, { successMessage: 'Invoice voided' });
      if (id) {
        const updatedInvoices = await apiGet<InvoiceRow[]>(`/api/projects/${id}/invoices`);
        setInvoices(updatedInvoices);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void invoice');
    } finally {
      setBusyInvoiceId(null);
    }
  }

  const invoiceDraftTotal = useMemo(() => {
    const subtotal = lineItems.reduce((sum, item) => {
      const q = Number(item.quantity);
      const p = Number(item.unit_price);
      return Number.isNaN(q) || Number.isNaN(p) ? sum : sum + q * p;
    }, 0);
    const tax = Number(invoiceTaxAmount);
    return { subtotal, total: subtotal + (Number.isNaN(tax) ? 0 : tax) };
  }, [lineItems, invoiceTaxAmount]);

  const financialSummary = useMemo(() => {
    const byCurrency = new Map<string, CurrencyFinancialSummary>();

    for (const invoice of invoices) {
      const currency = invoice.currency.toUpperCase();
      const summary = byCurrency.get(currency) ?? {
        currency,
        totalInvoiced: 0,
        totalPaid: 0,
        outstanding: 0,
      };

      summary.totalInvoiced += invoice.total;
      if (invoice.status === 'paid') {
        summary.totalPaid += invoice.total;
      }
      if (invoice.status === 'sent' || invoice.status === 'overdue') {
        summary.outstanding += invoice.total;
      }

      byCurrency.set(currency, summary);
    }

    const totalsByCurrency = Array.from(byCurrency.values()).sort((a, b) =>
      a.currency.localeCompare(b.currency),
    );

    return {
      totalsByCurrency,
      hasOutstanding: totalsByCurrency.some((item) => item.outstanding > 0),
    };
  }, [invoices]);

  if (!id) return <p className="text-sm text-red-600">Missing project id.</p>;

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'invoices', label: `Invoices (${invoices.length})` },
    { key: 'usage', label: 'Usage' },
    { key: 'settings', label: 'Settings' },
  ];

  const availableTransitions = project ? allowedTransitions[project.status] : [];

  return (
    <div>
      <Link to="/projects" className="text-sm text-brand-700 hover:underline">
        &larr; Projects
      </Link>

      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}

      {project ? (
        <>
          <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{project.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <ProjectStatusBadge status={project.status} />
                <span>&middot;</span>
                <span>{clientInfo?.display_name ?? project.client_id}</span>
                <span>&middot;</span>
                <span className="capitalize">{project.billing_type.replace('_', ' ')}</span>
                <span>&middot;</span>
                <span className="capitalize">{project.billing_cycle.replace('_', ' ')}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {availableTransitions.map((nextStatus) => (
                <button
                  key={nextStatus}
                  type="button"
                  disabled={transitionBusy}
                  onClick={() => {
                    void handleTransition(nextStatus);
                  }}
                  className={transitionButtonClass(nextStatus)}
                >
                  {transitionLabels[nextStatus] ?? nextStatus}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 border-b border-slate-200">
            <nav className="flex gap-0">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === tab.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="mt-4">
            {activeTab === 'overview' ? (
              <OverviewTab
                project={project}
                clientInfo={clientInfo}
                financialSummary={financialSummary}
                usageSummary={usageSummary}
                invoices={invoices}
              />
            ) : null}

            {activeTab === 'invoices' ? (
              <InvoicesTab
                invoices={invoices}
                showForm={showInvoiceForm}
                onToggleForm={() => setShowInvoiceForm((p) => !p)}
                lineItems={lineItems}
                setLineItems={setLineItems}
                lineItemTemplateSelections={lineItemTemplateSelections}
                setLineItemTemplateSelections={setLineItemTemplateSelections}
                itemTemplates={itemTemplates}
                invoiceIssueDate={invoiceIssueDate}
                setInvoiceIssueDate={setInvoiceIssueDate}
                invoiceDueDate={invoiceDueDate}
                setInvoiceDueDate={setInvoiceDueDate}
                invoiceCurrency={invoiceCurrency}
                setInvoiceCurrency={setInvoiceCurrency}
                invoiceTaxAmount={invoiceTaxAmount}
                setInvoiceTaxAmount={setInvoiceTaxAmount}
                invoiceNotes={invoiceNotes}
                setInvoiceNotes={setInvoiceNotes}
                invoiceInternalNotes={invoiceInternalNotes}
                setInvoiceInternalNotes={setInvoiceInternalNotes}
                draftTotal={invoiceDraftTotal}
                creatingInvoice={creatingInvoice}
                busyInvoiceId={busyInvoiceId}
                onCreateInvoice={handleCreateInvoice}
                onSend={sendInvoice}
                onVoid={voidInvoice}
              />
            ) : null}

            {activeTab === 'usage' ? (
              <UsageTab
                usageSummary={usageSummary}
                openAiMonthlyUsage={openAiMonthlyUsage}
                openAiMonthlyLoading={openAiMonthlyLoading}
                projectId={project.id}
                openAiPeriodPreset={openAiPeriodPreset}
                onChangeOpenAiPeriod={setOpenAiPeriodPreset}
              />
            ) : null}

            {activeTab === 'settings' ? (
              <SettingsTab
                editName={editName}
                setEditName={setEditName}
                editDesc={editDesc}
                setEditDesc={setEditDesc}
                editBillingType={editBillingType}
                setEditBillingType={setEditBillingType}
                editBillingCycle={editBillingCycle}
                setEditBillingCycle={setEditBillingCycle}
                editMarkup={editMarkup}
                setEditMarkup={setEditMarkup}
                editCap={editCap}
                setEditCap={setEditCap}
                editFixedTotal={editFixedTotal}
                setEditFixedTotal={setEditFixedTotal}
                saving={saving}
                onSave={handleSaveSettings}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function OverviewTab(props: {
  project: Project;
  clientInfo: ClientInfo | null;
  financialSummary: { totalsByCurrency: CurrencyFinancialSummary[]; hasOutstanding: boolean };
  usageSummary: UsageSummary | null;
  invoices: InvoiceRow[];
}) {
  const { project, clientInfo, financialSummary, usageSummary, invoices } = props;
  const recentInvoices = invoices.slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard
          title="Total Invoiced"
          value={formatCurrencySummaries(financialSummary.totalsByCurrency, 'totalInvoiced')}
          accent="slate"
        />
        <SummaryCard
          title="Total Paid"
          value={formatCurrencySummaries(financialSummary.totalsByCurrency, 'totalPaid')}
          accent="emerald"
        />
        <SummaryCard
          title="Outstanding"
          value={formatCurrencySummaries(financialSummary.totalsByCurrency, 'outstanding')}
          accent={financialSummary.hasOutstanding ? 'amber' : 'slate'}
        />
        <SummaryCard
          title="Usage Cost (30d)"
          value={formatUsd(usageSummary?.totals.billable_amount_usd ?? 0)}
          accent="blue"
        />
      </div>

      {financialSummary.totalsByCurrency.length > 1 ? (
        <p className="text-xs text-slate-500">
          Multi-currency totals are displayed per currency (not cross-currency summed).
        </p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700">Project Details</h3>
          <div className="mt-3 space-y-2 text-sm">
            <InfoRow label="Client" value={clientInfo?.display_name ?? project.client_id} />
            <InfoRow label="Description" value={project.description ?? '-'} />
            <InfoRow
              label="Billing"
              value={`${project.billing_type.replace('_', ' ')} / ${project.billing_cycle.replace('_', ' ')}`}
            />
            <InfoRow
              label="Markup"
              value={project.markup_pct !== null ? `${Math.round(project.markup_pct * 100)}%` : '-'}
            />
            {project.monthly_cap_usd ? (
              <InfoRow label="Monthly Cap" value={formatUsd(project.monthly_cap_usd)} />
            ) : null}
            {project.fixed_total_usd ? (
              <InfoRow label="Fixed Total" value={formatUsd(project.fixed_total_usd)} />
            ) : null}
            <InfoRow label="Created" value={shortDate(project.created_at)} />
            {project.started_at ? (
              <InfoRow label="Started" value={shortDate(project.started_at)} />
            ) : null}
            {project.completed_at ? (
              <InfoRow label="Completed" value={shortDate(project.completed_at)} />
            ) : null}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700">Recent Invoices</h3>
          {recentInvoices.length > 0 ? (
            <div className="mt-3 space-y-2">
              {recentInvoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between text-sm">
                  <Link
                    to={`/invoices/${inv.id}`}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {inv.invoice_number}
                  </Link>
                  <div className="flex items-center gap-2">
                    <InvoiceStatusBadge status={inv.status} />
                    <span className="font-medium">{formatMoney(inv.total, inv.currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">No invoices yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function InvoicesTab(props: {
  invoices: InvoiceRow[];
  showForm: boolean;
  onToggleForm: () => void;
  lineItems: InvoiceLineDraft[];
  setLineItems: React.Dispatch<React.SetStateAction<InvoiceLineDraft[]>>;
  lineItemTemplateSelections: Record<string, string>;
  setLineItemTemplateSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  itemTemplates: ItemTemplate[];
  invoiceIssueDate: string;
  setInvoiceIssueDate: (v: string) => void;
  invoiceDueDate: string;
  setInvoiceDueDate: (v: string) => void;
  invoiceCurrency: string;
  setInvoiceCurrency: (v: string) => void;
  invoiceTaxAmount: string;
  setInvoiceTaxAmount: (v: string) => void;
  invoiceNotes: string;
  setInvoiceNotes: (v: string) => void;
  invoiceInternalNotes: string;
  setInvoiceInternalNotes: (v: string) => void;
  draftTotal: { subtotal: number; total: number };
  creatingInvoice: boolean;
  busyInvoiceId: string | null;
  onCreateInvoice: (e: FormEvent<HTMLFormElement>) => void;
  onSend: (id: string) => void;
  onVoid: (id: string) => void;
}) {
  const {
    invoices,
    showForm,
    onToggleForm,
    lineItems,
    setLineItems,
    lineItemTemplateSelections,
    setLineItemTemplateSelections,
    itemTemplates,
    invoiceIssueDate,
    setInvoiceIssueDate,
    invoiceDueDate,
    setInvoiceDueDate,
    invoiceCurrency,
    setInvoiceCurrency,
    invoiceTaxAmount,
    setInvoiceTaxAmount,
    invoiceNotes,
    setInvoiceNotes,
    invoiceInternalNotes,
    setInvoiceInternalNotes,
    draftTotal,
    creatingInvoice,
    busyInvoiceId,
    onCreateInvoice,
    onSend,
    onVoid,
  } = props;

  const currencyOptions = ['USD', 'HKD', 'AUD', 'GBP', 'EUR', 'CNY', 'SGD'];

  function upsertLineItem(id: string, patch: Partial<InvoiceLineDraft>): void {
    setLineItems((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function handleTemplateChange(lineId: string, selected: string): void {
    setLineItemTemplateSelections((prev) => ({ ...prev, [lineId]: selected }));
    if (selected === 'custom') {
      return;
    }

    const selectedTemplate = itemTemplates.find((template) => template.id === selected);
    if (!selectedTemplate) {
      return;
    }

    upsertLineItem(lineId, {
      item_name: selectedTemplate.name,
      unit_price:
        selectedTemplate.default_unit_price !== null
          ? String(selectedTemplate.default_unit_price)
          : '',
    });
  }

  function addLineItem(): void {
    const nextId = rowId();
    setLineItems((prev) => [
      ...prev,
      { id: nextId, item_name: '', description: '', quantity: '1', unit_price: '' },
    ]);
    setLineItemTemplateSelections((prev) => ({ ...prev, [nextId]: 'custom' }));
  }

  function removeLineItem(lineId: string): void {
    setLineItems((prev) => prev.filter((row) => row.id !== lineId));
    setLineItemTemplateSelections((prev) => {
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Invoices</h3>
        <button
          type="button"
          onClick={onToggleForm}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
        >
          {showForm ? 'Cancel' : 'Create Invoice'}
        </button>
      </div>

      {showForm ? (
        <form
          onSubmit={onCreateInvoice}
          className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-4"
        >
          <h4 className="text-sm font-semibold text-slate-800">New Draft Invoice</h4>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium">
              Currency
              <select
                value={invoiceCurrency}
                onChange={(e) => setInvoiceCurrency(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                {currencyOptions.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              Issue date
              <input
                type="date"
                value={invoiceIssueDate}
                onChange={(e) => setInvoiceIssueDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm font-medium">
              Due date
              <input
                type="date"
                value={invoiceDueDate}
                onChange={(e) => setInvoiceDueDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Line items</p>
            {lineItems.map((item) => (
              <div
                key={item.id}
                className="grid gap-2 md:grid-cols-[1fr_1fr_80px_100px_100px_auto]"
              >
                <div className="space-y-1">
                  <select
                    value={lineItemTemplateSelections[item.id] ?? 'custom'}
                    onChange={(e) => handleTemplateChange(item.id, e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="custom">Custom</option>
                    {itemTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  <input
                    value={item.item_name}
                    onChange={(e) => {
                      upsertLineItem(item.id, { item_name: e.target.value });
                      setLineItemTemplateSelections((prev) => ({ ...prev, [item.id]: 'custom' }));
                    }}
                    placeholder="Item name"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <input
                  value={item.description}
                  onChange={(e) => upsertLineItem(item.id, { description: e.target.value })}
                  placeholder="Description"
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={item.quantity}
                  onChange={(e) => upsertLineItem(item.id, { quantity: e.target.value })}
                  placeholder="Qty"
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unit_price}
                  onChange={(e) => upsertLineItem(item.id, { unit_price: e.target.value })}
                  placeholder="Unit price"
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700">
                  {formatMoney(
                    (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
                    invoiceCurrency,
                  )}
                </div>
                <button
                  type="button"
                  disabled={lineItems.length === 1}
                  onClick={() => removeLineItem(item.id)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addLineItem}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              + Add line item
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium">
              Tax amount
              <input
                type="number"
                min="0"
                step="0.01"
                value={invoiceTaxAmount}
                onChange={(e) => setInvoiceTaxAmount(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
              <p>Subtotal: {formatMoney(draftTotal.subtotal, invoiceCurrency)}</p>
              <p className="mt-1 font-semibold">
                Total: {formatMoney(draftTotal.total, invoiceCurrency)}
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm font-medium">
              Notes (shown on invoice)
              <textarea
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium">
              Internal notes
              <textarea
                value={invoiceInternalNotes}
                onChange={(e) => setInvoiceInternalNotes(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={creatingInvoice}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {creatingInvoice ? 'Creating...' : 'Create Draft Invoice'}
          </button>
        </form>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
              <th className="px-3 py-2">Invoice</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Currency</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Issue Date</th>
              <th className="px-3 py-2">Due Date</th>
              <th className="px-3 py-2">Payment</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2">
                  <Link
                    to={`/invoices/${inv.id}`}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {inv.invoice_number}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <InvoiceStatusBadge status={inv.status} />
                </td>
                <td className="px-3 py-2 font-medium">{inv.currency.toUpperCase()}</td>
                <td className="px-3 py-2 text-right font-medium">
                  {formatMoney(inv.total, inv.currency)}
                </td>
                <td className="px-3 py-2">{shortDate(inv.issue_date)}</td>
                <td className="px-3 py-2">{shortDate(inv.due_date)}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {inv.paid_at
                    ? `Paid ${shortDate(inv.paid_at)}`
                    : inv.sent_at
                      ? `Sent ${shortDate(inv.sent_at)}`
                      : 'Not sent'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {inv.status === 'draft' ? (
                      <button
                        type="button"
                        onClick={() => {
                          void onSend(inv.id);
                        }}
                        disabled={busyInvoiceId === inv.id}
                        className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                      >
                        Send
                      </button>
                    ) : null}
                    {inv.status === 'draft' || inv.status === 'sent' || inv.status === 'overdue' ? (
                      <button
                        type="button"
                        onClick={() => {
                          void onVoid(inv.id);
                        }}
                        disabled={busyInvoiceId === inv.id}
                        className="rounded border border-rose-300 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        Void
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                  No invoices for this project yet
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsageTab(props: {
  usageSummary: UsageSummary | null;
  openAiMonthlyUsage: OpenAiProjectMonthlyUsage | null;
  openAiMonthlyLoading: boolean;
  projectId: string;
  openAiPeriodPreset: OpenAiPeriodPreset;
  onChangeOpenAiPeriod: (preset: OpenAiPeriodPreset) => void;
}) {
  const {
    usageSummary,
    openAiMonthlyUsage,
    openAiMonthlyLoading,
    projectId,
    openAiPeriodPreset,
    onChangeOpenAiPeriod,
  } = props;
  const totals = usageSummary?.totals ?? {
    raw_cost_usd: 0,
    billable_amount_usd: 0,
    requests_count: 0,
    input_tokens: 0,
    output_tokens: 0,
  };
  const by_model = usageSummary?.by_model ?? [];
  const hasStoredOpenAiUsage =
    openAiMonthlyUsage !== null &&
    (openAiMonthlyUsage.monthly.length > 0 || openAiMonthlyUsage.totals.requests_count > 0);
  const liveEstimate =
    openAiMonthlyUsage?.live_preview && openAiMonthlyUsage.live_preview.status === 'ok'
      ? openAiMonthlyUsage.live_preview.estimated_cost
      : null;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard title="Raw Cost" value={formatUsd(totals.raw_cost_usd)} accent="slate" />
        <SummaryCard title="Billable" value={formatUsd(totals.billable_amount_usd)} accent="blue" />
        <SummaryCard
          title="Requests"
          value={totals.requests_count.toLocaleString()}
          accent="slate"
        />
        <SummaryCard
          title="Tokens"
          value={`${totals.input_tokens.toLocaleString()} in / ${totals.output_tokens.toLocaleString()} out`}
          accent="slate"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
              <th className="px-3 py-2">Provider</th>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2 text-right">Requests</th>
              <th className="px-3 py-2 text-right">Input Tokens</th>
              <th className="px-3 py-2 text-right">Output Tokens</th>
              <th className="px-3 py-2 text-right">Raw Cost</th>
              <th className="px-3 py-2 text-right">Billable</th>
            </tr>
          </thead>
          <tbody>
            {by_model.map((row, idx) => (
              <tr
                key={`${row.provider}-${row.model}-${idx}`}
                className="border-b border-slate-100 last:border-0"
              >
                <td className="px-3 py-2 capitalize">{row.provider}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.model}</td>
                <td className="px-3 py-2 text-right">{row.total_requests.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{row.total_input.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{row.total_output.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{formatUsd(row.total_raw)}</td>
                <td className="px-3 py-2 text-right font-medium">
                  {formatUsd(row.total_billable)}
                </td>
              </tr>
            ))}
            {by_model.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No usage data
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-blue-800">
            OpenAI Monthly Cost (Mapped to this project)
          </h4>
          <div className="flex items-center gap-3">
            <select
              className="rounded-md border border-blue-300 bg-white px-2 py-1 text-xs text-blue-800"
              value={openAiPeriodPreset}
              disabled={openAiMonthlyLoading}
              onChange={(event) => {
                onChangeOpenAiPeriod(event.target.value as OpenAiPeriodPreset);
              }}
            >
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="last_3_months">Last 3 months</option>
            </select>
            <Link
              to={`/settings?tab=apiKeys&project_id=${encodeURIComponent(projectId)}`}
              className="text-xs font-medium text-blue-700 underline"
            >
              Manage OpenAI mappings
            </Link>
            {openAiMonthlyLoading ? (
              <span className="inline-flex items-center gap-1 text-xs text-blue-800">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-300 border-t-blue-700" />
                Loading OpenAI diagnostics...
              </span>
            ) : null}
          </div>
        </div>
        <p className="mt-1 text-xs text-blue-800">
          Data is based on mapped OpenAI identifiers in Settings → API Key Mappings.
        </p>

        {openAiMonthlyUsage ? (
          <>
            {openAiMonthlyUsage.live_preview ? (
              openAiMonthlyUsage.live_preview.status === 'ok' ? (
                <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                  Live OpenAI check matched {openAiMonthlyUsage.live_preview.matched_rows} row(s) in
                  this range (
                  {openAiMonthlyUsage.live_preview.totals.requests_count.toLocaleString()} requests,{' '}
                  {openAiMonthlyUsage.live_preview.totals.input_tokens.toLocaleString()} in /{' '}
                  {openAiMonthlyUsage.live_preview.totals.output_tokens.toLocaleString()} out
                  tokens).
                </div>
              ) : (
                <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                  Live OpenAI check failed: {openAiMonthlyUsage.live_preview.error}
                </div>
              )
            ) : null}

            {!hasStoredOpenAiUsage && liveEstimate ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Live OpenAI data exists but hourly collector has not persisted this range yet.
                Showing live estimated costs for visibility.
              </div>
            ) : null}

            <div className="mt-3 grid gap-3 md:grid-cols-4">
              <SummaryCard
                title="OpenAI Raw Cost"
                value={formatUsd(
                  hasStoredOpenAiUsage
                    ? openAiMonthlyUsage.totals.raw_cost_usd
                    : (liveEstimate?.raw_cost_usd ?? 0),
                )}
                accent="slate"
              />
              <SummaryCard
                title="OpenAI Billable"
                value={formatUsd(
                  hasStoredOpenAiUsage
                    ? openAiMonthlyUsage.totals.billable_amount_usd
                    : (liveEstimate?.billable_amount_usd ?? 0),
                )}
                accent="blue"
              />
              <SummaryCard
                title="OpenAI Requests"
                value={openAiMonthlyUsage.totals.requests_count.toLocaleString()}
                accent="slate"
              />
              <SummaryCard
                title="OpenAI Tokens"
                value={`${openAiMonthlyUsage.totals.input_tokens.toLocaleString()} in / ${openAiMonthlyUsage.totals.output_tokens.toLocaleString()} out`}
                accent="slate"
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {(openAiMonthlyUsage.mappings ?? []).map((mapping, idx) => (
                <span
                  key={`${mapping.key_identifier}-${idx}`}
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${mapping.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}
                >
                  {mapping.label ? `${mapping.label}: ` : ''}
                  {mapping.key_identifier}
                </span>
              ))}
              {(openAiMonthlyUsage.mappings ?? []).length === 0 ? (
                <span className="text-xs text-amber-700">
                  No active OpenAI mapping for this project yet.
                </span>
              ) : null}
            </div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-blue-100 bg-white">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-blue-100 bg-blue-50 text-left text-blue-900">
                    <th className="px-3 py-2">Month</th>
                    <th className="px-3 py-2 text-right">Requests</th>
                    <th className="px-3 py-2 text-right">Tokens (in/out)</th>
                    <th className="px-3 py-2 text-right">Raw Cost</th>
                    <th className="px-3 py-2 text-right">Billable</th>
                  </tr>
                </thead>
                <tbody>
                  {openAiMonthlyUsage.monthly.map((row) => (
                    <tr key={row.month} className="border-b border-blue-50 last:border-0">
                      <td className="px-3 py-2">{formatYearMonth(row.month)}</td>
                      <td className="px-3 py-2 text-right">
                        {row.total_requests.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {row.total_input_tokens.toLocaleString()} /{' '}
                        {row.total_output_tokens.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right">{formatUsd(row.total_raw_usd)}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatUsd(row.total_billable_usd)}
                      </td>
                    </tr>
                  ))}
                  {openAiMonthlyUsage.monthly.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                        No OpenAI usage found for mapped identifiers in selected period.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-600">
            {openAiMonthlyLoading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-700" />
                Fetching OpenAI monthly usage...
              </span>
            ) : (
              'OpenAI project usage is unavailable right now.'
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatYearMonth(value: string): string {
  const [year, month] = value.split('-');
  if (!year || !month) {
    return value;
  }

  const yearNumber = Number.parseInt(year, 10);
  const monthNumber = Number.parseInt(month, 10);
  if (
    Number.isNaN(yearNumber) ||
    Number.isNaN(monthNumber) ||
    monthNumber < 1 ||
    monthNumber > 12
  ) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(yearNumber, monthNumber - 1, 1)));
}

function formatCurrencySummaries(
  summaries: CurrencyFinancialSummary[],
  key: 'totalInvoiced' | 'totalPaid' | 'outstanding',
): string {
  if (summaries.length === 0) {
    return '$0.00';
  }

  if (summaries.length === 1) {
    const entry = summaries[0];
    if (!entry) {
      return '$0.00';
    }
    return formatMoney(entry[key], entry.currency);
  }

  return summaries.map((entry) => formatMoneyWithCode(entry[key], entry.currency)).join(' · ');
}

function formatMoneyWithCode(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }).format(value);
}

function SettingsTab(props: {
  editName: string;
  setEditName: (v: string) => void;
  editDesc: string;
  setEditDesc: (v: string) => void;
  editBillingType: string;
  setEditBillingType: (v: string) => void;
  editBillingCycle: string;
  setEditBillingCycle: (v: string) => void;
  editMarkup: string;
  setEditMarkup: (v: string) => void;
  editCap: string;
  setEditCap: (v: string) => void;
  editFixedTotal: string;
  setEditFixedTotal: (v: string) => void;
  saving: boolean;
  onSave: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const {
    editName,
    setEditName,
    editDesc,
    setEditDesc,
    editBillingType,
    setEditBillingType,
    editBillingCycle,
    setEditBillingCycle,
    editMarkup,
    setEditMarkup,
    editCap,
    setEditCap,
    editFixedTotal,
    setEditFixedTotal,
    saving,
    onSave,
  } = props;

  return (
    <form onSubmit={onSave} className="space-y-4">
      <div className="rounded-lg border border-slate-200 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700">General</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm font-medium">
            Project Name
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm font-medium">
            Description
            <textarea
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700">Billing Configuration</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm font-medium">
            Billing Type
            <select
              value={editBillingType}
              onChange={(e) => setEditBillingType(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="usage_based">Usage Based</option>
              <option value="fixed_price">Fixed Price</option>
              <option value="time_material">Time &amp; Material</option>
            </select>
          </label>
          <label className="text-sm font-medium">
            Billing Cycle
            <select
              value={editBillingCycle}
              onChange={(e) => setEditBillingCycle(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="monthly">Monthly</option>
              <option value="milestone">Milestone</option>
              <option value="on_demand">On Demand</option>
            </select>
          </label>
          <label className="text-sm font-medium">
            Markup % (0–100)
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={editMarkup}
              onChange={(e) => setEditMarkup(e.target.value)}
              placeholder="e.g. 30"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm font-medium">
            Monthly Cap (USD)
            <input
              type="number"
              min="0"
              step="0.01"
              value={editCap}
              onChange={(e) => setEditCap(e.target.value)}
              placeholder="Optional"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          {editBillingType === 'fixed_price' ? (
            <label className="text-sm font-medium">
              Fixed Total (USD)
              <input
                type="number"
                min="0"
                step="0.01"
                value={editFixedTotal}
                onChange={(e) => setEditFixedTotal(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          ) : null}
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </form>
  );
}

function SummaryCard(props: { title: string; value: string; accent: string }) {
  const colors: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    blue: 'border-blue-200 bg-blue-50',
    slate: 'border-slate-200 bg-slate-50',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[props.accent] ?? colors.slate}`}>
      <p className="text-sm text-slate-600">{props.title}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{props.value}</p>
    </div>
  );
}

function InfoRow(props: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{props.label}</span>
      <span className="font-medium text-slate-900">{props.value}</span>
    </div>
  );
}

function InvoiceStatusBadge(props: { status: InvoiceStatus }) {
  const styles: Record<InvoiceStatus, string> = {
    draft: 'bg-purple-50 text-purple-700',
    sent: 'bg-blue-50 text-blue-700',
    paid: 'bg-emerald-50 text-emerald-700',
    overdue: 'bg-amber-50 text-amber-700',
    void: 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[props.status]}`}>
      {props.status}
    </span>
  );
}

function ProjectStatusBadge(props: { status: ProjectStatus }) {
  const styles: Record<ProjectStatus, string> = {
    draft: 'bg-slate-100 text-slate-700',
    active: 'bg-emerald-50 text-emerald-700',
    paused: 'bg-amber-50 text-amber-700',
    completed: 'bg-blue-50 text-blue-700',
    archived: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[props.status]}`}>
      {props.status}
    </span>
  );
}

function transitionButtonClass(status: string): string {
  if (status === 'active')
    return 'rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50';
  if (status === 'paused')
    return 'rounded-md bg-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50';
  if (status === 'completed')
    return 'rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50';
  return 'rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50';
}

function toIsoFromDateInput(value: string): string | null {
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function todayDateValue(offsetDays = 0): string {
  const now = new Date();
  now.setUTCDate(now.getUTCDate() + offsetDays);
  return now.toISOString().slice(0, 10);
}

function rowId(): string {
  return `row_${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`;
}

function resolveOpenAiRange(preset: OpenAiPeriodPreset): { from: string; to: string } {
  const now = new Date();
  const utcNow = new Date(now.toISOString());

  const startOfCurrentMonth = new Date(
    Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1, 0, 0, 0, 0),
  );

  if (preset === 'this_month') {
    return {
      from: startOfCurrentMonth.toISOString(),
      to: utcNow.toISOString(),
    };
  }

  if (preset === 'last_month') {
    const startOfLastMonth = new Date(
      Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1, 0, 0, 0, 0),
    );
    const endOfLastMonth = new Date(startOfCurrentMonth.getTime() - 1);
    return {
      from: startOfLastMonth.toISOString(),
      to: endOfLastMonth.toISOString(),
    };
  }

  const startOfThreeMonthWindow = new Date(
    Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 2, 1, 0, 0, 0, 0),
  );
  return {
    from: startOfThreeMonthWindow.toISOString(),
    to: utcNow.toISOString(),
  };
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}
